import type { WalkState } from '@/components/WalkProgress';

/** How a read of the sealed toolset currently stands. */
export type ReadState = 'pending' | 'success' | 'error';

export interface WalkEvidence {
  /** A seal is known, from the projection or the stream's terminal event. */
  sealed: boolean;
  /** Whether the sealed-toolset read succeeded, failed, or is still in flight. */
  toolsetRead: ReadState;
  /** The walk stream's own verdict. Only it can report "no walk". */
  stream: WalkState;
  /** When the scope's listing last completed, or null if it never has. */
  observedAt: number | null;
  /** Now, injectable so the rule is testable without a clock. */
  now?: number;
}

/** The walk sweep's cadence; a scope inside it is waiting its turn, not stuck. */
const SWEEP_WINDOW_MINUTES = 20;

/**
 * Whether a scope can be said to have never been walked.
 *
 * This is a claim about the backend, so the backend has to make it, twice
 * over: a sealed-toolset read that SUCCEEDED and carries no seal, and the
 * walk stream reporting no walk. A read that failed or has not answered is
 * not evidence - the toolset route times out frequently, and treating that
 * silence as "never walked" reported a sealed bucket as blocked.
 */
export function isUnwalked({ sealed, toolsetRead, stream, observedAt, now = Date.now() }: WalkEvidence): boolean {
  if (sealed) return false;
  if (toolsetRead !== 'success') return false;
  if (stream !== 'none') return false;
  if (observedAt === null) return false;
  return now - observedAt >= SWEEP_WINDOW_MINUTES * 60_000;
}
